/**
 * Utilities for mapping Polymarket price-hit events to PolyStrike matrix format
 */

import type { GammaEvent, GammaMarket } from './polymarketApi'

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

export interface OrderInfo {
  orderPrice: number
  sizeMatched: number
  sizeTotal: number
}

export interface PositionInfo {
  size: number
  avgPrice: number
}

export interface MatrixCell {
  id: string
  rangeLabel: string
  yesTokenId: string
  noTokenId: string
  yesPrice: number
  noPrice: number
  tickSize: string
  negRisk: boolean
  orderInfo?: OrderInfo
  positionInfo?: PositionInfo
  orderInfoNo?: OrderInfo
  positionInfoNo?: PositionInfo
}

/** Пустая ячейка для колонки без рынка на дату */
export function createEmptyCell(rangeLabel: string, colIndex: number): MatrixCell {
  return {
    id: `empty-${rangeLabel}-col${colIndex}`,
    rangeLabel,
    yesTokenId: '',
    noTokenId: '',
    yesPrice: 0,
    noPrice: 0,
    tickSize: '0.001',
    negRisk: false,
  }
}

/** Парсит rangeLabel в [low, high]. "64,000-66,000" | "<58,000" | ">76,000" */
export function parsePriceRange(rangeLabel: string): { low: number; high: number } | null {
  const s = rangeLabel.replace(/,/g, '').trim()
  const rangeMatch = s.match(/^([\d.]+)\s*[-–]\s*([\d.]+)$/) // 58000-60000
  if (rangeMatch) {
    const low = parseFloat(rangeMatch[1])
    const high = parseFloat(rangeMatch[2])
    if (!isNaN(low) && !isNaN(high)) return { low, high }
  }
  const ltMatch = s.match(/^<\s*([\d.]+)$/) // <58000
  if (ltMatch) {
    const high = parseFloat(ltMatch[1])
    if (!isNaN(high)) return { low: 0, high }
  }
  const gtMatch = s.match(/^>\s*([\d.]+)$/) // >76000
  if (gtMatch) {
    const low = parseFloat(gtMatch[1])
    if (!isNaN(low)) return { low, high: Infinity }
  }
  return null
}

function parseTokenIds(clobTokenIds: string | string[]): [string, string] {
  try {
    const arr: string[] = Array.isArray(clobTokenIds)
      ? clobTokenIds
      : (JSON.parse(clobTokenIds as string) as string[])
    return [arr[0] ?? '', arr[1] ?? '']
  } catch {
    return ['', '']
  }
}

function parseOutcomePrices(outcomePrices: string | string[]): [number, number] {
  try {
    const arr: string[] = Array.isArray(outcomePrices)
      ? outcomePrices
      : (JSON.parse(outcomePrices as string) as string[])
    const yes = parseFloat(arr[0] ?? '0.5')
    const no = parseFloat(arr[1] ?? '0.5')
    return [yes, no]
  } catch {
    return [0.5, 0.5]
  }
}

export function gammaMarketToCell(market: GammaMarket): MatrixCell | null {
  if (market.closed) return null
  const [yesTokenId, noTokenId] = parseTokenIds(market.clobTokenIds)
  const [outcomeYes, outcomeNo] = parseOutcomePrices(market.outcomePrices)
  const yesPrice = outcomeYes
  const noPrice = outcomeNo
  const label = (market.groupItemTitle ?? market.question.slice(0, 15)).replace(/[↑↓]/g, '').trim()
  return {
    id: market.id,
    rangeLabel: label,
    yesTokenId,
    noTokenId,
    yesPrice,
    noPrice,
    tickSize: String(market.orderPriceMinTickSize ?? 0.001),
    negRisk: market.negRisk ?? false,
  }
}

/** Returns rows (price ranges) × cols (days). For single event: N rows × 1 col. Use expandTo3Cols for Today/+1/+2. */
export function gammaEventToMatrix(event: GammaEvent | { markets?: GammaMarket[] } | null): MatrixCell[][] {
  const ev = event as { markets?: GammaMarket[] } | null
  if (!ev?.markets?.length) return []
  const cells: MatrixCell[] = []
  for (const m of ev.markets) {
    const cell = gammaMarketToCell(m)
    if (cell && cell.yesTokenId && cell.noTokenId) cells.push(cell)
  }
  cells.sort((a, b) => {
    const aNum = parseFloat(a.rangeLabel.replace(/[^0-9.]/g, '')) || 0
    const bNum = parseFloat(b.rangeLabel.replace(/[^0-9.]/g, '')) || 0
    return bNum - aNum
  })
  return cells.map((c) => [c])
}

/** Expand 1-col matrix to 3 cols (Today, +1, +2). Replicates same event data when only weekly events exist. */
export function expandTo3Cols(matrix: MatrixCell[][]): MatrixCell[][] {
  if (!matrix.length) return []
  return matrix.map((row) => {
    const cell = row[0]
    if (!cell) return row
    return [cell, cell, cell]
  })
}

/** Канонический slug daily range: Polymarket {crypto}-price-on-{month}-{day} */
export function buildSlugsForDay(crypto: string, date: Date): string[] {
  const c = crypto.toLowerCase()
  const m = MONTHS[date.getMonth()]
  const d = date.getDate()
  return [`${c}-price-on-${m}-${d}`]
}

export function getWeekRangeForDayOffset(dayOffset: number): { start: Date; end: Date } {
  const now = new Date()
  const start = new Date(now)
  start.setDate(now.getDate() + dayOffset)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)
  return { start, end }
}
